import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { noop } from "lodash";

import ObjectCard from "~/stream/SideBar/CRMPanel/Salesforce/ObjectCard";

import type { SalesforceCardsQuery, SalesforceCardsQuery$data } from "~/__generated__/SalesforceCardsQuery.graphql";

import styles from "./CompanyPanel.module.css";

const salesforceCardsQuery = graphql`
	query SalesforceCardsQuery($emails: [String!]!) {
		salesforceObjectsForEmails(emails: $emails) {
			__typename
			name
			label
			value
			id
			url

			fields {
				name
				label
				value
				type
				interactionType
				length
			}

			references {
				name
				label
				value
				id
				url

				fields {
					name
					label
					value
					type
					interactionType
					length
				}
			}
		}
		viewer {
			account {
				integrations {
					salesforce {
						isConnected
					}
				}
			}
		}
	}
`;

type SalesforceObject = NonNullable<SalesforceCardsQuery$data["salesforceObjectsForEmails"]>[number];
type SalesforceEmailObject = Omit<SalesforceObject, "__typename" | "references">;
type SalesforceReferenceObject = SalesforceObject["references"][number];

export default function SalesforceCards({ emails }: { emails: string[] }) {
	const salesforceData = useLazyLoadQuery<SalesforceCardsQuery>(
		salesforceCardsQuery,
		{ emails },
		{ fetchPolicy: "store-and-network" },
	);

	const salesforceWorkspaceConfigurationEnabled = salesforceData?.viewer.account.integrations.salesforce?.isConnected;

	const cards = React.useMemo(() => {
		let salesforceCards: { [key: string]: SalesforceEmailObject[] } = {};
		let orderedSalesforceCards: SalesforceEmailObject[] = [];

		salesforceData?.salesforceObjectsForEmails?.forEach((salesforceObject: SalesforceObject) => {
			// Add salesforceObject only if it doesn't already exist in the array
			if (!salesforceCards[salesforceObject.label]?.some(obj => obj.id === salesforceObject.id)) {
				(salesforceCards[salesforceObject.label] ??= []).push(salesforceObject);
			}

			salesforceObject.references.forEach((reference: SalesforceReferenceObject) => {
				// Add reference only if it doesn't already exist in the array
				if (!salesforceCards[reference.label]?.some(obj => obj.id === reference.id)) {
					(salesforceCards[reference.label] ??= []).push(reference);
				}
			});
		});

		if (!salesforceWorkspaceConfigurationEnabled) {
			// If there is no workspace configuration, we want to follow a specific order of cards
			const priorityOrder = ["Account", "Contact", "Opportunity", "Case"];

			// First add the priority keys (if they exist)
			priorityOrder.forEach(key => {
				if (salesforceCards[key]) {
					orderedSalesforceCards.push(...salesforceCards[key]);
				}
			});

			// Then add any remaining keys in alphabetical order
			Object.keys(salesforceCards)
				.filter(key => !priorityOrder.includes(key))
				.sort()
				.forEach(key => {
					orderedSalesforceCards.push(...salesforceCards[key]);
				});
		} else {
			orderedSalesforceCards = Object.values(salesforceCards).flat();
		}

		return orderedSalesforceCards;
	}, [salesforceData?.salesforceObjectsForEmails, salesforceWorkspaceConfigurationEnabled]);

	return (
		<>
			{cards.length > 0 ? (
				cards.map(card => (
					<div className={styles.salesforceCard} key={card.id}>
						<ObjectCard
							{...card}
							canOpenDetails={true}
							handleRecordChange={noop}
							undoTrigger={0}
							forceViewOnly
							isOpen={false}
						/>
					</div>
				))
			) : (
				<div className={styles.crmEmptyState}>
					<p>No Salesforce objects found</p>
				</div>
			)}
		</>
	);
}
